import React, { Component } from "react"
import { connect } from "react-redux"
import { Field, formValueSelector, reduxForm } from "redux-form"
import { Arwes, Col, Loading } from "arwes"
import { MarginTop } from "../../common/styled/MarginTop"
import styled from "styled-components"

// Login form core, restyled per the TURN 3 "AUTH UPLINK" design
// (grid-replies-preview-enhancement, 3A). The panel chrome around it
// lives in core/Home.js.

const FieldLabel = styled.div`
  font-family: "VT323", monospace;
  font-size: 14px;
  letter-spacing: 2px;
  color: #00e7ff;
  margin-bottom: 6px;
`

const ThemedInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  background: #02121a;
  border: 1px solid rgba(0, 231, 255, 0.4);
  color: #00e7ff;
  font-family: "VT323", monospace;
  font-size: 18px;
  letter-spacing: 1px;
  padding: 10px 12px;
  outline: none;

  &:focus {
    border-color: #00e7ff;
    box-shadow: 0 0 8px rgba(0, 231, 255, 0.4);
  }

  &::placeholder {
    color: rgba(0, 231, 255, 0.35);
  }
`

const PwWrap = styled.div`
  position: relative;

  input {
    padding-right: 60px;
  }
`

const PwToggle = styled.span`
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-family: "VT323", monospace;
  font-size: 13px;
  letter-spacing: 1px;
  color: #be26fc;
  cursor: pointer;

  &:hover {
    color: #fc26fa;
  }
`

const OptionsRow = styled.div`
  display: flex;
  align-items: center;
  margin-top: 12px;
`

const Remember = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 7px;
  cursor: pointer;

  .glyph {
    font-family: "VT323", monospace;
    font-size: 16px;
    color: #00e7ff;
  }
  .label {
    font-family: "VT323", monospace;
    font-size: 14px;
    color: #6d7da6;
  }
`

const Forgot = styled.span`
  margin-left: auto;
  font-family: "VT323", monospace;
  font-size: 13px;
  color: #be26fc;
  cursor: pointer;

  &:hover {
    color: #fc26fa;
  }
`

const RecoveryNote = styled.div`
  margin-top: 8px;
  font-family: "VT323", monospace;
  font-size: 13px;
  letter-spacing: 1px;
  color: #ffb800;
`

const AccessDenied = styled.div`
  margin-top: 12px;
  border: 1px solid rgba(255, 0, 28, 0.6);
  background: rgba(255, 0, 28, 0.08);
  padding: 8px 12px;
  font-family: "VT323", monospace;
  font-size: 14px;
  letter-spacing: 1px;
  color: #ff001c;
`

const LoginButton = styled.button`
  margin-top: 18px;
  width: 100%;
  height: 46px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 231, 255, 0.12);
  border: 1px solid rgba(0, 231, 255, 0.8);
  color: #00e7ff;
  font-family: "Rajdhani", "VT323", monospace;
  font-weight: 700;
  font-size: 17px;
  letter-spacing: 4px;
  cursor: pointer;

  &:hover {
    background: rgba(0, 231, 255, 0.22);
    box-shadow: 0 0 14px rgba(0, 231, 255, 0.5);
  }
`

const themedField = ({ input, type, placeholder }) => (
  <ThemedInput {...input} type={type} placeholder={placeholder} />
)

class SignInInternal extends Component {
  state = {
    showPw: false,
    remember: true,
    recoveryNote: false
  }

  togglePw = () => this.setState(prev => ({ showPw: !prev.showPw }))
  toggleRemember = () => this.setState(prev => ({ remember: !prev.remember }))
  // No recovery backend exists (yet) — the link stays per the design and
  // answers honestly instead of dead-ending.
  toggleRecovery = () =>
    this.setState(prev => ({ recoveryNote: !prev.recoveryNote }))

  render() {
    const { handleSubmit, user } = this.props
    if (user.isLoading) {
      return (
        <Arwes>
          <MarginTop />
          <Col s={12} m={8} l={6} offset={["m2", "l3"]}>
            <Loading full animate />
          </Col>
        </Arwes>
      )
    }
    return (
      <form onSubmit={handleSubmit}>
        <FieldLabel>EMAIL</FieldLabel>
        <Field
          name="email"
          type="email"
          placeholder="user@uplink.net"
          component={themedField}
        />

        <FieldLabel style={{ margin: "14px 0 6px" }}>ACCESS CODE</FieldLabel>
        <PwWrap>
          <Field
            name="password"
            type={this.state.showPw ? "text" : "password"}
            placeholder="••••••••"
            component={themedField}
          />
          <PwToggle onClick={this.togglePw}>
            {this.state.showPw ? "HIDE" : "SHOW"}
          </PwToggle>
        </PwWrap>

        <OptionsRow>
          <Remember onClick={this.toggleRemember}>
            <span className="glyph">{this.state.remember ? "◉" : "○"}</span>
            <span className="label">REMEMBER THIS RIG</span>
          </Remember>
          <Forgot onClick={this.toggleRecovery}>FORGOT ACCESS CODE?</Forgot>
        </OptionsRow>
        {this.state.recoveryNote && (
          <RecoveryNote>
            // RECOVERY OFFLINE — contact the grid admin to reset your access
            code
          </RecoveryNote>
        )}

        {user.isError && (
          <AccessDenied>
            ACCESS DENIED // email and access code do not match
          </AccessDenied>
        )}

        <LoginButton type="submit">LOGIN ▸</LoginButton>
      </form>
    )
  }
}

const FORM_NAME = "loginForm"
const form = reduxForm({ form: FORM_NAME })(SignInInternal)
const selector = formValueSelector(FORM_NAME)

const initialValues = {
  email: "guest@guest.com",
  password: "default123"
}
const mapStateToProps = state => {
  const values = selector(state, "data")
  return {
    formValues: values,
    user: state.user,
    initialValues: initialValues
  }
}

export default connect(mapStateToProps)(form)
